<script>
	import { onMount, onDestroy } from "svelte";
	import { tweened } from "svelte/motion";
	import { cubicOut } from "svelte/easing";
	import {
		Col,
		Row,
		Progress,
		Modal,
		ModalBody,
		ModalHeader,
	} from "@sveltestrap/sveltestrap";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import client from "../../../services/client";
	import JobsTable from "./JobsTable.svelte";
	import queryString from "query-string"
	import { filterParams, listQueryParams } from "../../../services/helpers";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import {
		errorMessage,
		sizeSelect,
		totalPages,
		loading,
		initPage,
		csvError,
		totalRecords,
		canAssign,
		cachedTaskLookups,
		cachedJobLookups,
	} from "../../../store/store";
	import {
		arrayToQueryParams,
		extractErrorMessage,
		importCsv,
		queryArrStrToNum,
	} from "src/utils/misc";
	import ConfirmModal from "../../../components/CustomModal/ConfirmModal.svelte";
	import CsvErrorModal from "../../../components/CsvErrorModal/CsvErrorModal.svelte";
	import Tabs from "../../../components/Tabs/Tabs.svelte";
	import JobsFilterForm from "./JobsFilterForm.svelte";
	import JobsHeaderButtons from "./JobsHeaderButtons.svelte";
	import { navigate } from "svelte-routing";
	import JobsBacklogHeaderButtons from "../JobsBacklog/JobsBacklogHeaderButtons.svelte";
	import JobsBacklogFilterForm from "../JobsBacklog/JobsBacklogFilterForm.svelte";
	import JobsBacklog from "../JobsBacklog/JobsBacklog.svelte";
	import { jobStatusFilter } from "src/utils/options";
	import { get } from "svelte/store";
	import {
		fetchTaskLookups,
		fetchJobLookups,
	} from "../../../services/lookupCache";

	let lookups = null;
	let jobLookups = null;
	$: lookups = $cachedTaskLookups;
	$: jobLookups = $cachedJobLookups;

	let data = [];
	let files;
	let open = false;
	let hasMounted = false;
	let confirmOpen = false;
	let evOpen = false;
	let filterErrorMsg = "Try a different set of filters";
	$: backlogTasks = [];
	$: vehicles = {};
	$: progressStatus = "";
	$: activeTab = parseInt(query.tab) || 1;
	$: hasOtherKeys = false;

	let backlogParams = {
		pageSize: 20,
		pageNumber: 1,
		sortCol: 0,
		sortDir: 1,
	};

	let progress = tweened(10, {
		delay: 0,
		duration: 5000,
		easing: cubicOut,
	});

	let searchParams = {
		pageNumber: 1,
		pageSize: 20,
		sortCol: 0, // priority = 0
		sortDir: 1, // 1 = desc, 0 = asc
	};

	let pageOptions = {
		pageSize: 20,
	};

	let query = queryString.parse(window.location.search);

	function fillFiles(e) {
		files = e;
	}

	function changeBacklogParams(params) {
		if (params === "") {
			backlogParams = {
				pageSize: 20,
				pageNumber: 1,
				sortCol: 0,
				sortDir: 1,
			};
			initPage.set(1);
			fetchBacklog();
		} else if (params) {
			let newParams = { ...backlogParams, ...params };
			for (let key in newParams) {
				if (
					Array.isArray(newParams[key]) &&
					newParams[key].length === 0
				) {
					delete newParams[key];
				}
			}
			fetchBacklog(newParams);
		}
	}

	function changeSearchParams(params) {
		if (params === "") {
			searchParams = {
				pageNumber: 1,
				pageSize: 20,
				sortCol: 0,
				sortDir: 1,
			};
			initPage.set(1);
			fetchData();
		} else if (params) {
			let newParams = { ...searchParams, ...params };
			for (let key in newParams) {
				if (Array.isArray(params[key]) && params[key].length === 0) {
					delete newParams[key];
				}
			}
			fetchData(newParams);
		}
	}

	function resetParams() {
		searchParams = {
			...searchParams,
			pageNumber: 1,
			pageSize: 20,
			sortCol: 0,
			sortDir: 1,
		};
		backlogParams = {
			...backlogParams,
			pageSize: 20,
			pageNumber: 1,
			sortCol: 0,
			sortDir: 1,
		};
	}

	async function fetchData(params) {
		// active tab ["open", "ongoing", "assigned", "paused"]
		// past tab ["completed", "incomplete", "cancelled"]
		try {
			if (!params) {
				params = queryString.parse(window.location.search);
				if (params.categoryIds) {
					params.categoryIds = queryArrStrToNum(params.categoryIds);
				}
				if (params.statuses) {
					params.statuses = params.statuses.split(",");
				}
				if (params.estateIds) {
					params.estateIds = queryArrStrToNum(params.estateIds);
				}
				if (params.regionIds) {
					params.regionIds = queryArrStrToNum(params.regionIds);
				}

				// parse sorting params
				if (params.sortCol) {
					params.sortCol = parseInt(params.sortCol);
				}
				if (params.sortDir) {
					params.sortDir = parseInt(params.sortDir);
				}
			}
			let payload = {};
			if (!params.statuses) {
				if (activeTab === 2) {
					payload.statuses = ["completed", "incomplete", "cancelled"];
				} else if (activeTab === 1) {
					payload.statuses = [
						"open",
						"ongoing",
						"assigned",
						"paused",
					];
				}
			}

			// default sorting params when not provided
			if (!params.sortCol && params.sortCol !== 0) {
				params.sortCol = 0;
			}
			if (!params.sortDir && params.sortDir !== 0) {
				params.sortDir = 1; // default to descending order
			}

			loading.set(true);
			let queryParams = queryString.stringify(arrayToQueryParams(params));
			navigate(`/jobs?${queryParams}`);
			const res = await client("/v1/Jobs/search", {
				method: "POST",
				version: "Tasking",
				data: {
					...payload,
					...params,
					pageSize: $sizeSelect,
				},
			});
			params.tab = activeTab.toString();
			searchParams = params;

			if (res.status === 200) {
				loading.set(false);
				data = res.data.data;
				initPage.set(parseInt(res.data.pageNumber));
				totalRecords.set(res.data.totalCount);
				totalPages.set(res.data.totalPage);
				return;
			}
		} catch (error) {
			if (!(
				error.response.data.code || error.response.data.status === 400
			)) {
				extractErrorMessage(error);
				loading.set(false);
				return;
			}

			if (error.response.data.code === 29016) {
				filterErrorMsg = "Try a different set of filters";
			}

			if (
				error.response.data.errors &&
				error.response.data.errors.PlateNo[0]
			) {
				filterErrorMsg =
					"Please enter at least 3 characters for the most accurate search results.";
			}

			data = [];
			searchParams = params;
			canAssign.set(false);
			loading.set(false);
			setFilterError();
			return;
		}
	}

	async function fetchBacklog(params) {
		try {
			// For saving queries on refresh
			if (!params) {
				params = queryString.parse(window.location.search);
				if (params.vehicleSubstatusIds) {
					params.vehicleSubstatusIds = queryArrStrToNum(
						params.vehicleSubstatusIds
					);
				}
				if (params.presetTaskIds) {
					params.presetTaskIds = queryArrStrToNum(
						params.presetTaskIds
					);
				}
				if (params.priorities) {
					params.priorities = queryArrStrToNum(params.priorities);
				}
				if (params.regionIds) {
					params.regionIds = queryArrStrToNum(params.regionIds);
				}
				if (params.estateIds) {
					params.estateIds = queryArrStrToNum(params.estateIds);
				}
				// parse sorting params
				if (params.sortCol) {
					params.sortCol = parseInt(params.sortCol);
				}
				if (params.sortDir) {
					params.sortDir = parseInt(params.sortDir);
				}
			}
			// default sorting params when not provided
			if (!params.sortCol && params.sortCol !== 0) {
				params.sortCol = 0;
			}
			if (!params.sortDir && params.sortDir !== 0) {
				params.sortDir = 1;
			}

			// static page size for backlog
			sizeSelect.set("20");

			loading.set(true);
			let queryParams = queryString.stringify(arrayToQueryParams(params));
			navigate(`/jobs?${queryParams}`);
			const res = await client("/v1/Tasks/search", {
				method: "POST",
				version: "Tasking",
				data: {
					...params,
					pageSize: $sizeSelect,
				},
			});
			params.tab = 3;
			backlogParams = params;
			loading.set(false);
			if (res.status === 200) {
				backlogTasks = res.data.data;
				vehicles = res.data.vehicles;
				initPage.set(parseInt(res.data.pageNumber));
				totalRecords.set(res.data.totalCount);
				totalPages.set(res.data.totalPage);
				canAssign.set(true);
				return;
			}
			loading.set(false);
			return;
		} catch (error) {
			if (error.response.data.code === 29016) {
				backlogTasks = [];
				backlogParams = params;
				canAssign.set(false);
			} else {
				extractErrorMessage(error);
			}
			loading.set(false);
			return;
		}
	}

	function fetchActiveOrBacklog() {
		if (activeTab !== 3) {
			fetchData();
		} else {
			fetchBacklog();
		}
	}

	onMount(async () => {
		// populate lookup cache for tasks and jobs
		fetchTaskLookups();
		fetchJobLookups();

		await fetchActiveOrBacklog();
	});

	onDestroy(() => {
		unsubscribeSizeSelect();
		initPage.set(1);
		sizeSelect.set("20");
	});

	//function to call jobs api on select limit size
	const trigerSelectActiveJob = async (limit) => {
		fetchData(limit);
	};

	//function to handle priority sort for jobs
	const triggerPrioritySort = async () => {
		// if currently descending (1), switch to ascending (0) and vice versa
		const newSortDir = searchParams.sortDir === 1 ? 0 : 1;
		const newParams = {
			...searchParams,
			sortCol: 0,
			sortDir: newSortDir,
		};
		await fetchData(newParams);
	};

	//function to handle priority sort for tasks (backlog)
	const triggerSortBacklog = async () => {
		// if currently descending (1), switch to ascending (0) and vice versa
		const newSortDir = backlogParams.sortDir === 1 ? 0 : 1;
		backlogParams = {
			...backlogParams,
			sortCol: 0,
			sortDir: newSortDir,
		};

		await fetchBacklog(backlogParams);
	};

	//function to handle export to csv
	export const handleExportCSV = async (status) => {
		try {
			const res = await client("/v0/Job/Export", {
				method: "POST",
				version: "Tasking",
				header: "csv",
				data: {
					statuses: [0, 1, 5],
				},
			});

			const url = window.URL.createObjectURL(new Blob([res.data]));
			const link = document.createElement("a");
			link.href = url;
			link.setAttribute("download", "ActiveJobs.csv"); //or any other extension
			document.body.appendChild(link);
			link.click();

			// ;
		} catch (err) {
			console.log(err);
		}
	};

	//set job status to deleted
	async function handleDelete(jobId) {
		try {
			const res = await client("/v0/Job/UpdateStatus", {
				method: "POST",
				version: "Tasking",
				data: {
					jobId,
					status: 4,
				},
			});
			if (res.data.error_code === 0) {
				window.location.reload();
			}
		} catch (err) {
			console.log(err);
		}
	}

	function toggleCsvImport() {
		confirmOpen = !confirmOpen;
	}

	function toggleError() {
		csvError.set(false);
	}

	let errorOpen;
	csvError.subscribe((value) => {
		errorOpen = value;
	});

	let err;
	errorMessage.subscribe((value) => {
		err = value;
	});

	let items = [
		{
			label: "Active",
			value: 1,
		},
		{
			label: "Past",
			value: 2,
		},
		{
			label: "Backlog",
			value: 3,
		},
	];

	async function changeTab(index) {
		initPage.set(1);
		activeTab = index;
		searchParams = {
			pageNumber: 1,
			pageSize: 20,
			sortCol: 0,
			sortDir: 1,
		};
		backlogParams = {
			pageSize: 20,
			pageNumber: 1,
			sortCol: 0,
			sortDir: 1,
		};
		navigate(`/jobs?tab=${index}`);
		await fetchActiveOrBacklog();
	}

	const unsubscribeSizeSelect = sizeSelect.subscribe((value) => {
		if (!hasMounted) {
			hasMounted = true;
		} else if (pageOptions.pageSize !== value) {
			pageOptions.pageSize = value;
			resetParams();
			fetchActiveOrBacklog();
		}
	});

	function setFilterError() {
		query = queryString.parse(window.location.search);
		const allowedValues = ["tab", "pageNumber", "pageSize"];
		hasOtherKeys = Object.keys(query).some(
			(item) => !allowedValues.includes(item)
		);
	}
</script>

<Row>
	<Col xs={12}>
		<div>
			<ErrorMessageHandler message={err} />
			<Row>
				<Col class="row d-flex align-items-start mx-0">
					<PageHeader title="Jobs" />
				</Col>
				{#if activeTab !== 3}
					<JobsHeaderButtons
						{handleExportCSV}
						{confirmOpen}
						{files}
						{fillFiles}
						{toggleCsvImport}
					/>
				{:else}
					<JobsBacklogHeaderButtons />
				{/if}
			</Row>
			<Row>
				<Col>
					<Tabs {items} fetchJobs={changeTab} {activeTab} />
				</Col>
			</Row>
			<Row>
				{#if lookups && jobLookups}
					{#if activeTab !== 3}
						<JobsFilterForm
							{changeSearchParams}
							{lookups}
							{jobLookups}
							jobStatus={activeTab === 1
								? jobStatusFilter["active"]
								: jobStatusFilter["past"]}
							params={searchParams}
						/>
					{:else}
						<JobsBacklogFilterForm
							{changeBacklogParams}
							{lookups}
							{jobLookups}
							{backlogParams}
						/>
					{/if}
				{/if}
			</Row>
		</div>
	</Col>
	<Col>
		{#if activeTab !== 3}
			<JobsTable
				{data}
				{lookups}
				{handleDelete}
				{trigerSelectActiveJob}
				{triggerPrioritySort}
				{fetchData}
				{hasOtherKeys}
				{filterErrorMsg}
				pastTable={activeTab === 2}
				sortDir={searchParams.sortDir}
			/>
		{:else}
			<JobsBacklog
				{fetchBacklog}
				{triggerSortBacklog}
				{lookups}
				{backlogTasks}
				{vehicles}
				sortDir={backlogParams.sortDir}
			/>
		{/if}
	</Col>
</Row>
<div>
	<Modal isOpen={evOpen} size="lg" class="modal-dialog modal-dialog-centered">
		<ModalHeader>Importing EV Jobs</ModalHeader>
		<ModalBody>
			<p class="d-flex justify-content-center"><b>{progressStatus}</b></p>
			<Progress
				value={$progress}
				color={progress >= 100 ? "success" : "primary"}
			/>
		</ModalBody>
	</Modal>

	<Modal isOpen={open} size="lg" class="modal-dialog modal-dialog-centered">
		<ModalHeader>Importing Jobs and Automation of Blackout</ModalHeader>
		<ModalBody>
			<p class="d-flex justify-content-center"><b>{progressStatus}</b></p>
			<Progress
				value={$progress}
				color={progress >= 100 ? "success" : "primary"}
			/>
		</ModalBody>
	</Modal>
	<div id="output" />
</div>
<ConfirmModal
	open={confirmOpen}
	toggle={toggleCsvImport}
	{err}
	header="Upload file"
	bodyText="Do you want to upload this file?"
	buttonText="Upload"
	handleSubmit={() =>
		importCsv(
			files,
			"/v0/Job/v3/Import",
			"Tasking",
			toggleCsvImport,
			fetchData,
			() => (document.getElementById("fileInput").value = "")
		)}
/>
<CsvErrorModal open={errorOpen} toggle={toggleError} />
